import { inject, Injectable } from '@angular/core';
import {
  Conversation,
  ConversationSortField,
  ListConversationsRequestFilters,
  OverridePrivacyResponse,
  PrivacyLevel,
  SortDirection,
} from '@vendasta/protos/meeting_analysis/v1';
import { listConversations$, updateConversation$ } from '@vendasta/protos/meeting_analysis/v1/meeting-analysis';
import { overridePrivacy$ } from '@vendasta/protos/meeting_analysis/v1/meeting-bot-service';
import { catchError, combineLatest, map, Observable, of, switchMap } from 'rxjs';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {
  MEETING_ANALYSIS_NAMESPACE_INJECTION_TOKEN$,
  MEETING_ANALYSIS_NAMESPACE_NAME_INJECTION_TOKEN$,
  MEETING_ANALYSIS_USER_ID_INJECTION_TOKEN$,
} from '@galaxy/meeting-analysis/static';

@Injectable({
  providedIn: 'root',
})
export class MeetingListService {
  readonly snackbarService = inject(SnackbarService);

  private readonly namespace$ = inject(MEETING_ANALYSIS_NAMESPACE_INJECTION_TOKEN$);
  private readonly userId$ = inject(MEETING_ANALYSIS_USER_ID_INJECTION_TOKEN$);

  readonly namespaceName$ = inject(MEETING_ANALYSIS_NAMESPACE_NAME_INJECTION_TOKEN$);

  updateConversationVisibility(
    conversationId: string,
    namespace: string,
    newPrivacyLevel: PrivacyLevel,
  ): Observable<void> {
    if (!conversationId || !namespace || !newPrivacyLevel) {
      throw new Error('Conversation ID, namespace, or new privacy level is required');
    }
    return updateConversation$({
      conversation: {
        id: conversationId,
        namespace: namespace,
        privacyLevel: newPrivacyLevel,
      } as Conversation,
      fieldMask: {
        paths: ['PrivacyLevel'],
      },
    }).pipe(
      catchError((error) => {
        console.error('Error updating conversation visibility', error);
        throw error;
      }),
    );
  }

  updateMeetingBotVisibility(meetingBotId: string, newPrivacyLevel: PrivacyLevel): Observable<OverridePrivacyResponse> {
    if (!meetingBotId || newPrivacyLevel === null) {
      throw new Error('Meeting bot ID or new privacy level is required');
    }
    return overridePrivacy$({
      meetingBotId: meetingBotId,
      privacyLevel: newPrivacyLevel,
    }).pipe(
        catchError((error) => {
          console.error('Error overriding privacy level', error);
          throw error;
        }),
      );
  }

  listRecordings(searchTerm = '', pagesize?: number): Observable<Conversation[]> {
    return combineLatest([this.namespace$, this.userId$]).pipe(
      switchMap(([namespace, userId]) =>
        listConversations$({
          userId,
          filters: {
            namespace,
          } as ListConversationsRequestFilters,
          searchTerm,
          pagingOptions: {
            pageSize: pagesize || 50,
            cursor: '',
          },
          sort: {
            field: ConversationSortField.CONVERSATION_SORT_FIELD_MEETING_DATE,
            direction: SortDirection.SORT_DIRECTION_DESCENDING,
          },
        }),
      ),
      catchError(() => {
        this.snackbarService.openErrorSnack('MEETING_LIST.ERROR_LOADING_MEETINGS');
        return of(null);
      }),
      map((response) => response?.conversations || []),
    );
  }
}
